import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import SocialLoginButtons from '@/components/SocialLoginButtons';
import BrandWordmark from '@/components/BrandWordmark';

/**
 * Signup — three fields only: Username, Email, Password.
 *
 * The legacy "Name" field was removed when display_name was consolidated
 * into `username` as the sole public identity. Email + Username are both
 * required up front so users can log in with either from day one
 * (no awkward "go to profile and set a username" detour).
 */
export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Client-side mirror of the backend rule so the user gets instant
  // feedback before they hit submit. Username is REQUIRED — the
  // empty-string case shows a clean "please choose one" hint rather
  // than a hard error.
  const trimmedUsername = username.trim();
  const usernameError = (() => {
    if (!trimmedUsername) return null; // empty handled separately on submit
    if (trimmedUsername.length < 3 || trimmedUsername.length > 30) {
      return 'Username must be 3\u201330 characters';
    }
    if (!/^[A-Za-z0-9](?:[A-Za-z0-9_-]{1,28}[A-Za-z0-9])?$/.test(trimmedUsername)) {
      return 'Letters, numbers, _ and - only \u2014 no spaces';
    }
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    setSubmitting(true);
    try {
      // AuthContext.signup signature is (email, password, name, username).
      // We pass null for the legacy `name` argument — the user record
      // simply won't have a `name` field set, which is fine: the UI now
      // reads `username` everywhere. Username stays OPTIONAL — users can
      // add one later from Profile Edit if they didn't pick one at signup.
      await signup(email.trim(), password, null, trimmedUsername || null);
      toast.success('Welcome to GroovLens');
      // Honor `?next=` for shared-link signup flows.
      const next = searchParams.get('next');
      navigate(next && next.startsWith('/') ? next : '/upload', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-x-0 top-0 h-[40vh] pointer-events-none bg-crate-glow" aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <Link to="/" className="inline-flex items-center mb-6">
          <BrandWordmark size="md" />
        </Link>

        <h1 className="font-display text-2xl font-semibold mb-1">Start your collection</h1>
        <p className="text-sm text-muted-foreground mb-6">Free, takes 10 seconds.</p>

        <SocialLoginButtons mode="signup" />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="username">Username (optional)</Label>
            <Input
              id="username"
              type="text"
              placeholder="yourhandle"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              aria-invalid={!!usernameError}
              aria-describedby="username-hint"
              data-testid="signup-username-input"
            />
            <p
              id="username-hint"
              className={`text-xs mt-1 ${usernameError ? 'text-[hsl(var(--destructive))]' : 'text-muted-foreground'}`}
              data-testid="signup-username-hint"
            >
              {usernameError
                ? usernameError
                : 'Optional. Your public handle — letters, numbers, _ and - only. Can be added later from Profile.'}
            </p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@groovlens.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="signup-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="min 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="signup-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl"
            data-testid="signup-submit-button"
          >
            {submitting ? 'Creating…' : 'Create account'}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[hsl(var(--primary))] hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
